import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { Minus, Plus, Search, ShoppingCart, Loader2, ArrowLeft, Tag, Truck } from 'lucide-react';
import { fetchCategories, fetchProducts, API } from '@/lib/api';
import { useCart, formatAUD } from '@/context/CartContext';
import { CATEGORY_FALLBACK_IMG } from '@/lib/imageFallbacks';
import ChefsSpecialBanner from '@/components/site/ChefsSpecialBanner';
import Seo from '@/components/Seo';
import { resolveImage } from '@/lib/resolveImage';

const ALL = { slug: 'all', label: 'All categories' };

const slotsFetcher = (path) => fetch(`${API}${path}`).then((r) => r.json());

export default function OrderForm() {
  const { data: categories = [] } = useSWR('categories', fetchCategories);
  const { data: products = [], isLoading } = useSWR('products', () => fetchProducts());
  const { data: slots } = useSWR('/shop/slots', slotsFetcher);
  const [activeCat, setActiveCat] = useState('all');
  const [query, setQuery] = useState('');
  const { add, items, setQty, remove, subtotal, totalSavings, openDrawer, config } = useCart();

  // Live slot preview — earliest available across pickup / metro / regional
  const nextSlots = useMemo(() => {
    if (!slots?.modes) return null;
    const pick = (mode) => slots.modes[mode]?.dates?.[0]?.label || null;
    return {
      pickup: pick('pickup'),
      metro: pick('metro'),
      regional: pick('regional'),
    };
  }, [slots]);

  const cartMap = useMemo(() => {
    const m = new Map();
    items.forEach((i) => m.set(i.product_id, i));
    return m;
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter((p) => activeCat === 'all' || p.category === activeCat)
      .filter((p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.cut?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      )
      .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
  }, [products, activeCat, query]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((p) => {
      if (!g[p.category]) g[p.category] = [];
      g[p.category].push(p);
    });
    return g;
  }, [filtered]);

  const bulkKg = config?.bulk_threshold_kg ?? 10;
  const bulkPct = Math.round((config?.bulk_discount_pct ?? 0.1) * 100);

  const onAdjust = (product, delta) => {
    const existing = cartMap.get(product.id);
    const step = Number(product.step_qty) || (product.unit === 'kg' ? 0.5 : 1);
    const min = Number(product.min_qty) || step;
    if (!existing) {
      if (delta > 0) add(product, min);
      return;
    }
    const next = +(existing.quantity + delta * step).toFixed(2);
    if (next < min) {
      remove(product.id);
    } else {
      setQty(product.id, next);
    }
  };

  const onDirect = (product, value) => {
    const step = Number(product.step_qty) || (product.unit === 'kg' ? 0.5 : 1);
    const min = Number(product.min_qty) || step;
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) {
      remove(product.id);
      return;
    }
    const existing = cartMap.get(product.id);
    if (!existing) add(product, Math.max(min, num));
    else setQty(product.id, Math.max(min, num));
  };

  return (
    <div className="section-light min-h-screen bg-[var(--mw-bg)] text-white flex flex-col">
      <Seo
        title="Order Premium Meat Online"
        description="Order Halal-certified Australian beef, lamb, poultry and smallgoods online. Cold-chain delivery across Perth. Bulk discounts and subscription pricing available."
        keywords="order meat online Perth, halal meat delivery, wagyu online Australia, grass-fed beef delivery, subscription meat box, wholesale meat retail"
        path="/order"
      />
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[var(--mw-bg)]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="order-back-home"
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em]">Back</span>
            </Link>
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)]">Online order form</div>
              <h1 className="font-display text-2xl sm:text-3xl leading-tight mt-0.5">Quick order</h1>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3 flex-1 max-w-md ml-auto">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                placeholder="Search cuts…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="order-search"
                className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/15 text-sm placeholder:text-white/35 focus:outline-none focus:border-[var(--mw-primary)] transition-colors"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Category tabs */}
      <div className="border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 py-4 flex gap-2 overflow-x-auto scrollbar-none">
          {[ALL, ...categories].map((c) => {
            const active = activeCat === c.slug;
            return (
              <button
                key={c.slug}
                type="button"
                onClick={() => setActiveCat(c.slug)}
                data-testid={`order-cat-${c.slug}`}
                className={`shrink-0 px-4 py-2 text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                  active
                    ? 'border-[var(--mw-primary)] bg-[var(--mw-primary)] text-white'
                    : 'border-white/15 text-white/60 hover:border-white/30 hover:text-white'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk banner + Slot preview */}
      <div className="bg-emerald-500/5 border-b border-emerald-500/20 py-2.5">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] uppercase tracking-[0.22em]">
          <span className="text-emerald-300 flex items-center gap-2">
            <Tag size={13} />
            Order <span className="text-white font-medium">{bulkKg}kg+</span> for auto{' '}
            <span className="text-white font-medium">{bulkPct}% off</span>
          </span>
          {nextSlots?.pickup && (
            <span
              data-testid="order-slot-preview"
              className="text-white/60 flex items-center gap-2"
            >
              <Truck size={13} className="text-[var(--mw-primary)]" />
              <span className="hidden sm:inline">Next: pickup </span>
              <span className="text-white font-medium normal-case tracking-normal">{nextSlots.pickup}</span>
              {nextSlots.metro && (
                <>
                  <span className="text-white/30 mx-1">·</span>
                  <span className="hidden sm:inline">metro </span>
                  <span className="text-white font-medium normal-case tracking-normal">{nextSlots.metro}</span>
                </>
              )}
              {nextSlots.regional && (
                <>
                  <span className="text-white/30 mx-1">·</span>
                  <span className="hidden md:inline">regional </span>
                  <span className="text-white font-medium normal-case tracking-normal">{nextSlots.regional}</span>
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Chef's Special hero */}
      <ChefsSpecialBanner />

      {/* Table */}
      <main className="flex-1 pb-40">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 py-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-24">
              <Loader2 size={28} className="animate-spin text-white/50" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 text-white/50">
              No products match your search.
            </div>
          ) : (
            <div data-testid="order-table" className="space-y-10">
              {Object.entries(grouped).map(([catSlug, catProducts]) => {
                const cat = categories.find((c) => c.slug === catSlug);
                return (
                  <section key={catSlug} data-testid={`order-section-${catSlug}`}>
                    <div className="flex items-baseline justify-between border-b border-white/15 pb-3 mb-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)]">
                          {cat?.label || catSlug}
                        </div>
                        <div className="text-xs text-white/50 mt-0.5">{cat?.tagline}</div>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                        {catProducts.length} cut{catProducts.length === 1 ? '' : 's'}
                      </div>
                    </div>

                    {/* Table header */}
                    <div className="hidden md:grid grid-cols-12 gap-4 py-2 px-2 text-[9px] uppercase tracking-[0.28em] text-white/40 border-b border-white/10">
                      <div className="col-span-5">Product</div>
                      <div className="col-span-2 text-right">Price</div>
                      <div className="col-span-3 text-center">Quantity</div>
                      <div className="col-span-2 text-right">Line total</div>
                    </div>

                    {/* Rows */}
                    <ul className="divide-y divide-white/5">
                      {catProducts.map((p) => {
                        const line = cartMap.get(p.id);
                        const qty = line?.quantity || 0;
                        const isBulk = line?.isBulk;
                        const unitDisp = p.unit === 'kg' ? '/kg' : p.unit === 'pack' ? '/pack' : '/ea';
                        const lineTotal = line?.lineTotal || 0;
                        return (
                          <li
                            key={p.id}
                            data-testid={`order-row-${p.id}`}
                            className={`grid grid-cols-12 gap-4 py-3 px-2 items-center hover:bg-white/[0.02] transition-colors ${
                              qty > 0 ? 'bg-[var(--mw-primary)]/5' : ''
                            }`}
                          >
                            {/* Product */}
                            <div className="col-span-12 md:col-span-5 flex items-center gap-3">
                              <img
                                src={p.image_url ? resolveImage(p.image_url, 160) : resolveImage(CATEGORY_FALLBACK_IMG[p.category] || '', 160)}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                width="48"
                                height="48"
                                className="w-12 h-12 object-cover border border-white/10 shrink-0"
                              />
                              <div className="min-w-0">
                                <div className="font-display text-base leading-tight">{p.name}</div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5">
                                  {p.cut}
                                </div>
                              </div>
                            </div>
                            {/* Price */}
                            <div className="col-span-4 md:col-span-2 md:text-right">
                              <div className="font-display text-lg text-white tabular-nums">{formatAUD(p.price)}</div>
                              <div className="text-[9px] uppercase tracking-[0.22em] text-white/40">per {p.unit}</div>
                            </div>
                            {/* Quantity picker */}
                            <div className="col-span-8 md:col-span-3 flex justify-start md:justify-center">
                              <div className="inline-flex items-center border border-white/15">
                                <button
                                  type="button"
                                  onClick={() => onAdjust(p, -1)}
                                  data-testid={`order-qty-decrement-${p.id}`}
                                  disabled={qty === 0}
                                  className="w-9 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                  aria-label="Decrease"
                                >
                                  <Minus size={13} />
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  step={p.step_qty || (p.unit === 'kg' ? 0.5 : 1)}
                                  value={qty || ''}
                                  placeholder="0"
                                  onChange={(e) => onDirect(p, e.target.value)}
                                  data-testid={`order-qty-input-${p.id}`}
                                  className="w-16 h-10 bg-transparent text-white text-center text-sm outline-none border-x border-white/15 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => onAdjust(p, +1)}
                                  data-testid={`order-qty-increment-${p.id}`}
                                  className="w-9 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                                  aria-label="Increase"
                                >
                                  <Plus size={13} />
                                </button>
                                <span className="px-2 text-[10px] uppercase tracking-[0.22em] text-white/45">{p.unit}</span>
                              </div>
                            </div>
                            {/* Line total */}
                            <div className="col-span-12 md:col-span-2 md:text-right">
                              {qty > 0 ? (
                                <>
                                  <div
                                    data-testid={`order-line-total-${p.id}`}
                                    className="font-display text-lg text-white tabular-nums"
                                  >
                                    {formatAUD(lineTotal)}
                                  </div>
                                  {isBulk ? (
                                    <div className="text-[9px] uppercase tracking-[0.22em] text-emerald-400 mt-0.5">
                                      Bulk {bulkPct}% off · −{formatAUD(line.savings)}
                                    </div>
                                  ) : (
                                    p.unit === 'kg' && qty > 0 && qty < bulkKg && (bulkKg - qty) <= 3 && (
                                      <div
                                        data-testid={`order-bulk-coach-${p.id}`}
                                        className="text-[9px] uppercase tracking-[0.22em] text-amber-400/80 mt-0.5"
                                      >
                                        +{(bulkKg - qty).toFixed(1)}kg to unlock {bulkPct}% off
                                      </div>
                                    )
                                  )}
                                </>
                              ) : (
                                <span className="text-white/25 text-sm tabular-nums">—</span>
                              )}
                              <span className="sr-only">{unitDisp}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Sticky totals bar */}
      <div
        data-testid="order-sticky-bar"
        className={`fixed inset-x-0 bottom-0 z-30 border-t border-white/10 backdrop-blur-xl bg-black/80 transition-transform duration-300 ${
          items.length === 0 ? 'translate-y-full' : 'translate-y-0'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 py-4 flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-6">
            <div>
              <div className="text-[9px] uppercase tracking-[0.28em] text-white/45">Items</div>
              <div className="font-display text-xl tabular-nums" data-testid="order-count">{items.length}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.28em] text-white/45">Subtotal</div>
              <div className="font-display text-xl tabular-nums" data-testid="order-subtotal">{formatAUD(subtotal)}</div>
            </div>
            {totalSavings > 0 && (
              <div>
                <div className="text-[9px] uppercase tracking-[0.28em] text-emerald-400">Bulk savings</div>
                <div className="font-display text-xl tabular-nums text-emerald-400" data-testid="order-savings">
                  −{formatAUD(totalSavings)}
                </div>
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={openDrawer}
              data-testid="order-checkout-btn"
              className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-white text-[11px] uppercase tracking-[0.22em] px-6 py-3.5 transition-colors"
            >
              <ShoppingCart size={14} />
              Review & Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
